import { JSX } from 'preact';
import { MutableRef, useCallback, useEffect, useRef } from 'preact/hooks';

import { isTouchDevice } from '../../../utils/device';

const isRightClick = (e: MouseEvent) => {
  return e.button === 2;
};

export const useSliderDragging = ({
  sliderRef,
}: {
  sliderRef: MutableRef<HTMLElement>;
}) => {
  const isDragging = useRef(false);

  useEffect(() => {
    if (isTouchDevice) {
      return;
    }

    let dragStartPosition = 0;

    const interactDragHandler = (e: HTMLElementEventMap['mousemove']) => {
      isDragging.current = true;

      const { scrollLeft } = sliderRef.current;

      const offset = e.pageX - dragStartPosition;

      sliderRef.current.scrollLeft = scrollLeft - offset;

      dragStartPosition = e.pageX;
    };

    const interactEndHandler = () => {
      dragStartPosition = 0;

      window.removeEventListener('mousemove', interactDragHandler);
      window.removeEventListener('mouseup', interactEndHandler);

      window.setTimeout(() => {
        isDragging.current = false;
      });
    };

    const initInteractHandler = (e: HTMLElementEventMap['mousedown']) => {
      if (isRightClick(e)) {
        return;
      }

      dragStartPosition = e.pageX;

      window.addEventListener('mousemove', interactDragHandler, {
        passive: true,
      });
      window.addEventListener('mouseup', interactEndHandler);
    };

    const slider = sliderRef.current;

    slider.addEventListener('mousedown', initInteractHandler);

    return () => {
      slider.removeEventListener('mousedown', initInteractHandler);
      window.removeEventListener('mousemove', interactDragHandler);
      window.removeEventListener('mouseup', interactEndHandler);
    };
  }, []);

  const handleClickCapture: JSX.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      if (isDragging.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    []
  );

  return { handleClickCapture };
};
